/**
 * 导入云存储服务
 */
import { CloudStorageService } from './cloudStorage';
/**
 * 历史记录管理工具
 */
export class HistoryManager {
    /**
     * 初始化方法，在类加载时调用
     */
    static initialize() {
        // 初始化云存储服务
        CloudStorageService.initialize();
        // 检查云存储是否已启用
        this.isCloudEnabled = CloudStorageService.isAuth();
    }
    /**
     * 启用云存储
     * @param password 云存储密码
     * @returns 是否启用成功
     */
    static async enableCloudStorage(password) {
        try {
            const isAuthenticated = await CloudStorageService.authenticate(password);
            if (isAuthenticated) {
                this.isCloudEnabled = true;
                return true;
            }
            return false;
        }
        catch (error) {
            console.error('启用云存储失败:', error);
            return false;
        }
    }
    /**
     * 检查云存储是否已启用
     * @returns 云存储是否已启用
     */
    static isCloudStorageEnabled() {
        // 动态检查云存储状态，确保页面刷新后能正确恢复
        this.isCloudEnabled = CloudStorageService.isAuth();
        return this.isCloudEnabled;
    }
    /**
     * 禁用云存储
     */
    static disableCloudStorage() {
        this.isCloudEnabled = false;
        CloudStorageService.clearAuthentication();
    }
    /**
     * 从云端加载历史记录到本地
     */
    static async loadHistoryFromCloud() {
        // 动态检查云存储状态
        if (!this.isCloudStorageEnabled())
            return;
        try {
            console.log('开始从云端加载历史记录...');
            // 获取所有历史记录ID
            const ids = await CloudStorageService.getHistoryIds();
            console.log('获取到的历史记录ID列表:', ids);
            if (ids.length === 0) {
                console.log('云端没有历史记录');
                return;
            }
            // 批量获取历史记录
            const cloudHistory = await CloudStorageService.batchDownloadHistoryItems(ids);
            console.log('从云端获取的历史记录:', cloudHistory);
            console.log('历史记录数量:', cloudHistory.length);
            // 检查每条记录的格式
            cloudHistory.forEach((item, index) => {
                console.log(`记录 ${index}:`, item);
                console.log(`记录 ${index} 的类型:`, typeof item);
                console.log(`记录 ${index} 的键:`, Object.keys(item));
            });
            // 获取本地历史记录
            const localHistory = this.getHistory();
            console.log('本地历史记录:', localHistory);
            // 合并本地和云端的历史记录
            const mergedHistory = this.mergeHistories(localHistory, cloudHistory);
            console.log('合并后的历史记录:', mergedHistory);
            // 限制历史记录数量，最多保存50条
            if (mergedHistory.length > 50) {
                mergedHistory.splice(50);
            }
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(mergedHistory));
            console.log('历史记录已保存到本地');
        }
        catch (error) {
            console.error('从云端加载历史记录失败:', error);
        }
    }
    /**
     * 保存历史记录到云端
     */
    static async saveHistoryToCloud() {
        if (!this.isCloudEnabled)
            return;
        try {
            const history = this.getHistory();
            // 上传每个历史记录
            for (const item of history) {
                await CloudStorageService.uploadHistoryItem(item);
            }
        }
        catch (error) {
            console.error('保存历史记录到云端失败:', error);
        }
    }
    /**
     * 合并本地和云端的历史记录
     * @param localHistory 本地历史记录
     * @param cloudHistory 云端历史记录
     * @returns 合并后的历史记录
     */
    static mergeHistories(localHistory, cloudHistory) {
        console.log('开始合并历史记录...');
        console.log('本地历史记录数量:', localHistory.length);
        console.log('云端历史记录数量:', cloudHistory.length);
        const mergedMap = new Map();
        // 先添加云端历史记录
        cloudHistory.forEach(item => {
            mergedMap.set(item.id, item);
            console.log('添加云端记录:', item.id);
        });
        // 再添加本地历史记录，如果ID相同则保留时间戳较新的
        localHistory.forEach(item => {
            const existingItem = mergedMap.get(item.id);
            if (!existingItem) {
                mergedMap.set(item.id, item);
                console.log('添加本地记录（云端不存在）:', item.id);
            }
            else if (item.timestamp > existingItem.timestamp) {
                mergedMap.set(item.id, item);
                console.log('更新记录（本地版本更新）:', item.id);
            }
            else {
                console.log('跳过本地记录（云端版本更新）:', item.id);
            }
        });
        // 转换为数组并按时间戳降序排序
        const mergedHistory = Array.from(mergedMap.values()).sort((a, b) => b.timestamp - a.timestamp);
        console.log('合并后的历史记录数量:', mergedHistory.length);
        return mergedHistory;
    }
    /**
     * 保存历史记录到本地存储，如果云存储已启用则同步到云端
     * @param item 历史记录项
     */
    static async saveHistory(item) {
        try {
            const history = this.getHistory();
            const newHistoryItem = {
                ...item,
                id: this.generateId(),
                timestamp: Date.now(),
            };
            // 添加到历史记录开头
            history.unshift(newHistoryItem);
            // 限制历史记录数量，最多保存50条
            if (history.length > 50) {
                history.splice(50);
            }
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(history));
            // 动态检查云存储状态，如果已启用则保存到云端
            if (this.isCloudStorageEnabled()) {
                await CloudStorageService.uploadHistoryItem(newHistoryItem);
            }
        }
        catch (error) {
            console.error('保存历史记录失败:', error);
        }
    }
    /**
     * 从本地存储获取历史记录
     * @returns 历史记录数组
     */
    static getHistory() {
        try {
            const historyJson = localStorage.getItem(this.STORAGE_KEY);
            return historyJson ? JSON.parse(historyJson) : [];
        }
        catch (error) {
            console.error('获取历史记录失败:', error);
            return [];
        }
    }
    /**
     * 获取历史记录（如果启用了云存储，会自动从云端加载）
     * @returns 历史记录数组
     */
    static async getHistoryWithCloudSync() {
        // 动态检查云存储状态
        const isCloudEnabled = this.isCloudStorageEnabled();
        console.log('getHistoryWithCloudSync 被调用，云存储状态:', isCloudEnabled);
        // 如果云存储已启用，从云端加载
        if (isCloudEnabled) {
            console.log('开始从云端加载历史记录...');
            await this.loadHistoryFromCloud();
        }
        else {
            console.log('云存储未启用，只返回本地历史记录');
        }
        const localHistory = this.getHistory();
        console.log('返回的历史记录:', localHistory);
        return localHistory;
    }
    /**
     * 更新指定ID的历史记录
     * @param id 历史记录ID
     * @param updatedItem 更新后的历史记录数据
     */
    static async updateHistory(id, updatedItem) {
        try {
            const history = this.getHistory();
            const itemIndex = history.findIndex(item => item.id === id);
            if (itemIndex === -1) {
                console.error('未找到要更新的历史记录:', id);
                return;
            }
            // 更新历史记录，保留原有的ID和时间戳
            history[itemIndex] = {
                ...updatedItem,
                id: id,
                timestamp: history[itemIndex].timestamp,
            };
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(history));
            // 动态检查云存储状态，如果已启用则更新云端记录
            if (this.isCloudStorageEnabled()) {
                // 先删除云端记录，再上传更新后的记录
                await CloudStorageService.deleteHistoryItem(id);
                await CloudStorageService.uploadHistoryItem(history[itemIndex]);
            }
        }
        catch (error) {
            console.error('更新历史记录失败:', error);
        }
    }
    /**
     * 删除指定ID的历史记录
     * @param id 历史记录ID
     */
    static async deleteHistory(id) {
        try {
            const history = this.getHistory();
            const filteredHistory = history.filter(item => item.id !== id);
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredHistory));
            // 动态检查云存储状态，如果已启用则删除云端记录
            if (this.isCloudStorageEnabled()) {
                await CloudStorageService.deleteHistoryItem(id);
            }
        }
        catch (error) {
            console.error('删除历史记录失败:', error);
        }
    }
    /**
     * 清空所有历史记录
     */
    static async clearHistory() {
        try {
            localStorage.removeItem(this.STORAGE_KEY);
            // 动态检查云存储状态，如果已启用则清空云端记录
            if (this.isCloudStorageEnabled()) {
                await CloudStorageService.clearAllHistory();
            }
        }
        catch (error) {
            console.error('清空历史记录失败:', error);
        }
    }
    /**
     * 生成唯一ID
     * @returns 唯一ID字符串
     */
    static generateId() {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }
    /**
     * 格式化日期显示
     * @param timestamp 时间戳
     * @returns 格式化后的日期字符串
     */
    static formatDate(timestamp) {
        const date = new Date(timestamp);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    }
}
HistoryManager.STORAGE_KEY = 'iztro_history';
HistoryManager.isCloudEnabled = false;
